import {useEffect, useState} from "react";
import {Status} from "../models/enums";
import {useTranslation} from "react-i18next";
import {apiInstance} from "../api/api";
import LoggerService from "../services/LoggerService";
import NotificationService from "../services/NotificationService"
import AssetCustomField from "../models/AssetCustomField";
import {CustomFieldUpdateResult} from "../models/AssetCustomFieldUpdate";
import {CustomFieldData} from "../models/restModels";
import CustomFieldValues from "../models/CustomFieldValues";


function useAssetCustomFields(assetTypeId: number | undefined, siteId: number | undefined) {
    const {t} = useTranslation();
    const [loadStatus, setLoadStatus] = useState(Status.IDLE);
    const [assetCustomFields, setAssetCustomFields] = useState<AssetCustomField[]>([]);
    const [updateStatus, setUpdateStatus] = useState<Status>(Status.IDLE);

    useEffect(() => {
        async function loadAssetCustomFields() {
            setLoadStatus(Status.LOADING);

            try {
                if (assetTypeId && siteId) {
                    const fields =
                        await apiInstance.assetCustomFieldsApi.fetchAssetCustomFields(assetTypeId, siteId);

                    if (fields.length === 0) {
                        setLoadStatus(Status.NO_DATA)
                        return;
                    }

                    setAssetCustomFields(fields);
                    setLoadStatus(Status.SUCCESS);
                }
            } catch (err) {
                setLoadStatus(Status.ERROR);
                LoggerService.logError(err);
                NotificationService.errorNotification(
                    t("common.error"),
                    t("projects.summary.itemsTable.loadAssetCustomFields.loadErrorDesc")
                );
            }
        }

        loadAssetCustomFields();
    }, [assetTypeId, siteId, t]);

    interface CustomFieldValue {
        customFieldId: number;
        customFieldName: string;
        value: any;
    }

    const updateCustomField = async (
        siteId: number,
        assetId: number,
        customFieldId: number,
        customFieldName: string,
        value: any
    ) => {
        setUpdateStatus(Status.LOADING);

        try {
            const result: CustomFieldUpdateResult = await apiInstance.assetCustomFieldsApi.updateAssetCustomFields(
                siteId, assetId, customFieldId, value
            );

            if (result.success) {
                setUpdateStatus(Status.SUCCESS);
            } else {
                setUpdateStatus(Status.ERROR);
                NotificationService.errorNotification(
                    t("common.error"),
                    `${t("projects.summary.itemsTable.loadAssetCustomFields.saveError")} 
                    ${customFieldName}`

                );
            }
        } catch (err) {
            setUpdateStatus(Status.ERROR);
            LoggerService.logError(err);
            NotificationService.errorNotification(
                t("common.error"),
                t("projects.summary.itemsTable.loadAssetCustomFields.loadErrorDesc")
            );
        }
    };

    function getCustomFieldValuesByCountItem(countItemId: string, assetId: number): Promise<CustomFieldValues[]> {
        if (countItemId && assetId) {
            return apiInstance.assetCustomFieldsApi.fetchCustomFieldsByCountItem(countItemId, assetId)
        }
        return Promise.resolve([]);
    }

    function getCustomFieldValuesByItem(item: string, assetId: number) : Promise<CustomFieldValues[]> {
        if (item && assetId) {
            return apiInstance.assetCustomFieldsApi.fetchCustomFieldsByItem(item, assetId)
        }
        return Promise.resolve([]);
    }

    const postToCustomFieldTable = async (
        assetId: number,
        countItemId: string,
        customFieldValues: CustomFieldValue[],
        countGroupId: string,
    ) => {
        setUpdateStatus(Status.LOADING);
        const customFieldData = {
            itemId: countGroupId,
            countItemId: countItemId,
            assetId: assetId,
            customFields: JSON.stringify(
                customFieldValues.map(({customFieldId, customFieldName, value }) => ({
                    customFieldId: customFieldId,
                    customFieldName: customFieldName,
                    value: value
                }))
            ),
            siteId: siteId,
        };

        try {
            const result: CustomFieldUpdateResult = await apiInstance.assetCustomFieldsApi.postToCustomFieldTable(
                customFieldData
            );

            if (result.success) {
                setUpdateStatus(Status.SUCCESS);
            } else {
                setUpdateStatus(Status.ERROR);
                NotificationService.errorNotification(
                    t("common.error"),
                    `${t("projects.summary.itemsTable.loadAssetCustomFields.saveError")}
                ${customFieldData.customFields}`
                );
            }
        } catch (err) {
            setUpdateStatus(Status.ERROR);
            LoggerService.logError(err);
            NotificationService.errorNotification(
                t("common.error"),
                t("projects.summary.itemsTable.loadAssetCustomFields.loadErrorDesc")
            );
        }
    };

    const updateCustomFieldTable = async (
        customFieldId: string,
        assetId: number,
        itemId: string,
        countItemId: string,
        customFieldValues: CustomFieldValue[]
    ) => {
        const customFieldData: CustomFieldData = {
            itemId: itemId,
            countItemId: countItemId,
            assetId: assetId,
            customFields: JSON.stringify(
                customFieldValues.map(({customFieldId, customFieldName, value }) => ({
                    customFieldId: customFieldId,
                    customFieldName: customFieldName,
                    value: value
                }))
            )
        };
        setUpdateStatus(Status.LOADING);

        try {
            const result: CustomFieldUpdateResult = await apiInstance.assetCustomFieldsApi.updateCustomFieldTable(customFieldData);

            if (result.success) {
                setUpdateStatus(Status.SUCCESS);
            } else {
                setUpdateStatus(Status.ERROR);
                NotificationService.errorNotification(
                    t("common.error"),
                    `${t("projects.summary.itemsTable.loadAssetCustomFields.updateError")} 
                ${customFieldData.customFields || customFieldId}`
                );
            }
        } catch (err) {
            setUpdateStatus(Status.ERROR);
            LoggerService.logError(err);
            NotificationService.errorNotification(
                t("common.error"),
                t("projects.summary.itemsTable.loadAssetCustomFields.loadErrorDesc")
            );
        }
    };

    return { assetCustomFields, loadStatus, updateCustomField, getCustomFieldValuesByCountItem, updateCustomFieldTable, getCustomFieldValuesByItem, postToCustomFieldTable, updateStatus };
}

export default useAssetCustomFields;
